import React from "react";
import { Link } from "react-router-dom";
import { cash, dateRange, longDate, nightsLabel } from "../../../services/api";

const METHOD = {
  bank: { name: "Bank transfer", note: "We email the bank details with your reference." },
  card: { name: "Card", note: "Pay securely by debit or credit card." },
  crypto: { name: "Cryptocurrency", note: "Pay the amount due in crypto." },
};

/**
 * Step 5 — check everything, choose how to pay, reserve. Only the plans
 * and methods the admin has switched on are offered. A by-request room
 * skips payment entirely: nothing is charged until the team confirms.
 */
const StepReview = ({ d, set, options, quote, category, retreat, go, requestOnly, saveError }) => {
  const sessions = options.sessions;
  const chosen = Object.entries(d.addons).filter(([, q]) => q > 0)
    .map(([id, qty]) => ({ s: sessions.find((x) => x.id === Number(id)), qty })).filter((x) => x.s);
  const plans = quote?.options?.plans || [];
  const methods = quote?.options?.methods || [];
  const plan = plans.find((p) => p.id === d.payment_plan);
  const dietName = (id) => options.diets.find((x) => x.id === id)?.name;
  const holdHours = options.payment.holdHours;

  return (
    <section className="bk-step" aria-labelledby="s5">
      <h2 id="s5" className="bk-step-title">Review and reserve</h2>

      <div className="bk-review">
        <ReviewBlock title="Retreat" onEdit={() => go(1)}>
          <p><b>{retreat.title}</b></p>
          <p>{dateRange(quote?.arrive || retreat.startDate, quote?.depart || retreat.endDate)} · {nightsLabel(retreat.nights + d.before + d.after)}</p>
          <p>{d.guests} {d.guests === 1 ? "guest" : "guests"}</p>
          {d.extension_request && <p className="bk-muted">Asked for: {d.extension_request}</p>}
        </ReviewBlock>
        <ReviewBlock title="Room" onEdit={() => go(2)}>
          <p><b>{category?.name}</b></p>
          <p className="bk-muted">{category?.isBase ? "Included" : category?.requestOnly ? "By request" : `${cash(category?.upgradeForStay)} for your stay`}</p>
          <p className="bk-muted">{category?.mealService === "plated" ? "Plated meals" : "Buffet meals"}</p>
        </ReviewBlock>
        <ReviewBlock title="Add-ons" onEdit={() => go(3)}>
          {chosen.length ? (
            <ul className="bk-mini-list">{chosen.map(({ s, qty }) => <li key={s.id}>{s.name}{qty > 1 ? ` × ${qty}` : ""}</li>)}</ul>
          ) : <p className="bk-muted">None chosen</p>}
        </ReviewBlock>
      </div>

      <div className="bk-panel">
        <div className="bk-panel-head">
          <p className="bk-label">Guests</p>
          <button type="button" className="bk-edit" onClick={() => go(4)}>Edit</button>
        </div>
        <ol className="bk-guest-list">
          {d.people.map((g, i) => (
            <li key={i}>
              <b>{g.first_name} {g.last_name}</b>
              <span>{[g.email, g.phone, g.country].filter(Boolean).join(" · ")}</span>
              <span className="bk-muted">{g.diets.map(dietName).filter(Boolean).join(", ") || "No food preferences given"}{g.dietary_notes ? `. ${g.dietary_notes}` : ""}</span>
            </li>
          ))}
        </ol>
      </div>

      {requestOnly ? (
        <div className="bk-panel bk-screening">
          <p className="bk-label">How a request works</p>
          <p className="mb-0">{category.name} isn't booked instantly. We'll check availability with you, confirm the price, and only then ask for payment. Your place on the retreat isn't held until then.</p>
        </div>
      ) : (
        <>
          <fieldset className="bk-panel">
            <legend className="bk-label">How would you like to pay?</legend>
            <div className="bk-pay">
              {plans.map((p) => (
                <label key={p.id} className={`bk-pay-opt ${d.payment_plan === p.id ? "is-on" : ""}`}>
                  <input type="radio" name="plan" checked={d.payment_plan === p.id} onChange={() => set({ payment_plan: p.id })} />
                  <span>
                    <b>{p.id === "deposit" ? `Pay a deposit of ${cash(p.dueNow)}` : p.id === "plan" ? `Pay in ${p.schedule.length} instalments` : `Pay ${cash(p.dueNow)} in full`}</b>
                    <small>
                      {p.id === "deposit" && `The balance of ${cash(p.balance)} is due by ${longDate(p.balanceDueDate)}.`}
                      {p.id === "plan" && p.schedule.map((s) => `${cash(s.amount)} ${s.seq === 1 ? "today" : `by ${longDate(s.due_date)}`}`).join(", ")}
                      {p.id === "full" && "Nothing more to pay before you arrive."}
                    </small>
                  </span>
                </label>
              ))}
            </div>
            {methods.length > 1 && (
              <div className="bk-methods" role="radiogroup" aria-label="Payment method">
                {methods.map((m) => (
                  <label key={m} className={`bk-chip ${d.payment_method === m ? "is-on" : ""}`}>
                    <input type="radio" name="method" checked={d.payment_method === m} onChange={() => set({ payment_method: m })} />
                    {METHOD[m]?.name || m}
                  </label>
                ))}
              </div>
            )}
            {d.payment_method && <p className="bk-muted mb-0">{METHOD[d.payment_method]?.name}: {METHOD[d.payment_method]?.note}</p>}
          </fieldset>

          <div className="bk-panel bk-info">
            <p><b>Your place is held for {holdHours} hours.</b> It's confirmed as soon as {plan ? cash(plan.dueNow) : "your payment"} arrives. If it doesn't, the room and place are released.</p>
            <p><b>Medical screening is included.</b> Every guest completes it after booking. Some experiences need clearance before they're confirmed.</p>
            {options.cancellationSummary && <p className="mb-0"><b>Cancellations.</b> {options.cancellationSummary}</p>}
          </div>
        </>
      )}

      <label className="bk-agree">
        <input type="checkbox" checked={d.accept_terms} onChange={(e) => set({ accept_terms: e.target.checked })} />
        <span>I've read and agree to the <Link to="/terms" target="_blank">terms</Link> and the <Link to="/cancellation-policy" target="_blank">cancellation policy</Link>.</span>
      </label>

      {saveError && <p className="mk-error" role="alert">{saveError}</p>}
    </section>
  );
};

const ReviewBlock = ({ title, onEdit, children }) => (
  <div className="bk-panel bk-review-block">
    <div className="bk-panel-head">
      <p className="bk-label">{title}</p>
      <button type="button" className="bk-edit" onClick={onEdit}>Edit</button>
    </div>
    {children}
  </div>
);

export default StepReview;
